import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { navigateToAssessmentOrDashboard } from "../utils/assessmentNavigation";
import { Helmet } from "react-helmet";
import { useAuth } from "../context/AuthContext";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  BarChart3,
  Clock,
  Users,
  BookOpen,
  Shield,
} from "lucide-react";

const BCMInstructions = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [error] = useState("");
  const [loading, setLoading] = useState(true);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    const loadPrivacyStatus = async () => {
      try {
        const res = await api.get("maturity/privacy/current/");

        setPrivacyAccepted(
          res.data?.has_notice && res.data?.accepted
        );
      } catch (err) {
        console.error("Failed to load privacy status:", err);

        // If anything goes wrong, treat as not accepted
        setPrivacyAccepted(false);
      } finally {
        setLoading(false);
      }
    };

    loadPrivacyStatus();
  }, [authLoading, user, navigate]);

  // Safer field fallbacks (works across different user payload shapes)
  const stakeholderName =
    user?.name ||
    user?.full_name ||
    user?.username ||
    "";
  const stakeholderEmail = user?.email || "";
  const stakeholderDepartment =
    user?.department ||
    user?.stakeholder_profile?.department ||
    "N/A";

  const bcmPhases = [
    { title: "Establishing a BCMS", description: "Setting up the framework and governance structure", icon: "📐" },
    { title: "Embracing Business Continuity", description: "Building organizational awareness and commitment", icon: "🤝" },
    { title: "Analysis", description: "Assessing impacts and continuity requirements", icon: "📊" },
    { title: "Solutions Design", description: "Developing recovery strategies and solutions", icon: "🎯" },
    { title: "Enabling Solutions", description: "Implementing and deploying BC solutions", icon: "⚙️" },
    { title: "Validation", description: "Testing and validating BC capabilities", icon: "✅" },
  ];

  const steps = [
    {
      number: 1,
      title: "Read Carefully",
      description: "Review each question and its context thoroughly before answering.",
      icon: <BookOpen size={24} />
    },
    {
      number: 2,
      title: "Answer Honestly",
      description: "Base your responses on actual practices, not planned actions or aspirations.",
      icon: <CheckCircle2 size={24} />
    },
    {
      number: 3,
      title: "Provide Evidence",
      description: "Attach supporting documents, reports, or records where applicable.",
      icon: <FileText size={24} />
    },
    {
      number: 4,
      title: "Consult When Needed",
      description: "Work with colleagues if unsure about any question.",
      icon: <Users size={24} />
    },
    {
      number: 5,
      title: "Submit Responses",
      description: "Complete all responses and submit your assessment.",
      icon: <CheckCircle2 size={24} className="text-green-600" />
    },
    {
      number: 6,
      title: "Review Results",
      description: "Access your dashboard to view maturity scores and identified gaps.",
      icon: <BarChart3 size={24} />
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-amber-700 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50 to-orange-50">
      <Helmet>
        <title>Instructions | BCM Assessment</title>
      </Helmet>

      <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-12 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 flex items-center gap-3">
            <BookOpen size={40} />
            BCM Assessment Guide
          </h1>
          <p className="text-amber-100 text-lg">Complete the Business Continuity Management maturity assessment</p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-xl shadow-lg border-l-4 border-amber-600 p-8 mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Information</h2>
            <Users className="text-amber-600" size={32} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Name</label>
              <input
                type="text"
                value={stakeholderName}
                readOnly
                className="w-full p-3 border border-blue-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Name"
              />
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Email</label>
              <input
                type="email"
                value={stakeholderEmail}
                readOnly
                className="w-full p-3 border border-green-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Email"
              />
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg border border-purple-200">
              <label className="text-sm font-semibold text-gray-700 block mb-2">Department</label>
              <input
                type="text"
                value={stakeholderDepartment}
                readOnly
                className="w-full p-3 border border-purple-300 rounded-lg bg-white font-medium text-gray-900"
                aria-label="Stakeholder Department"
              />
            </div>
          </div>

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-blue-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <BookOpen className="text-blue-600" size={28} />
            About This Assessment
          </h2>

          <div className="prose prose-sm max-w-none text-gray-700 space-y-4 mb-8">
            <p className="text-lg">
              Welcome to the <span className="font-bold text-amber-700">Business Continuity Management (BCM)</span> Maturity Assessment. This comprehensive evaluation helps determine your organization's preparedness for business disruptions.
            </p>

            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <p className="font-semibold text-gray-900 mb-3">What is BCM?</p>
              <p className="text-gray-700">
                Business Continuity Management is a holistic management process that identifies potential threats to an organization and the impacts to business operations if those threats occur. It provides a framework for building organizational resilience and recovery capabilities.
              </p>
            </div>

            <p className="font-semibold text-gray-900">Assessment Goals:</p>
            <ul className="grid md:grid-cols-2 gap-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Identify current BCM maturity level</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Discover operational gaps and weaknesses</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Guide strategic improvements and investments</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                <span>Support compliance and governance requirements</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-purple-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <BarChart3 className="text-purple-600" size={28} />
            Assessment Phases
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bcmPhases.map((phase, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg border border-gray-200 hover:shadow-md transition">
                <div className="text-4xl mb-3">{phase.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{phase.title}</h3>
                <p className="text-sm text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-gray-700">
              The assessment covers all six phases of BCM maturity, from establishing governance through validation and continuous improvement.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-green-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <FileText className="text-green-600" size={28} />
            Question Components
          </h2>

          <div className="space-y-6">
            {[
              {
                label: "BCM Phase",
                description: 'The maturity phase of business continuity under review (e.g., "Establishing a BCMS")',
                example: "Phase: Establishing a BCMS"
              },
              {
                label: "Focus Area",
                description: "The specific domain within the phase being assessed (e.g., governance, strategy, coordination)",
                example: "Focus Area: BCM Governance & Commitment"
              },
              {
                label: "Question/Control",
                description: "The specific practice, process, or control being evaluated",
                example: "Is there a formal BCM policy document approved by senior management?"
              },
              {
                label: "Your Response",
                description: 'Answer "Yes" (we have this implemented) or "No" (we do not have this)',
                example: "Yes / No"
              },
              {
                label: "Supporting Evidence",
                description: 'If your answer is "Yes" you must attach supporting documents, reports, or any evidence (required for submission). If your answer is "No", no evidence is required.',
                example: "Policy documents, audit reports, test results, etc."
              },
            ].map((item, idx) => (
              <div key={idx} className="border-l-4 border-green-400 pl-6 py-4">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">{item.label}</h3>
                <p className="text-gray-700 mb-2">{item.description}</p>
                <div className="bg-green-50 p-3 rounded text-sm text-green-900 border border-green-200">
                  <span className="font-semibold">Example:</span> {item.example}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12 border-l-4 border-orange-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <Clock className="text-orange-600" size={28} />
            How to Complete the Assessment
          </h2>

          <div className="space-y-4">
            {steps.map((step, idx) => (
              <div key={idx} className="flex gap-6 p-6 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:border-orange-300 transition">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange-100 border-2 border-orange-600">
                    <span className="text-xl font-bold text-orange-600">{step.number}</span>
                  </div>
                </div>
                <div className="flex-grow">
                  <h3 className="font-bold text-gray-900 text-lg mb-1">{step.title}</h3>
                  <p className="text-gray-700">{step.description}</p>
                </div>
                <div className="flex-shrink-0 hidden sm:flex items-center text-orange-400">
                  {step.icon}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-amber-50 rounded-xl shadow-lg p-8 mb-12 border-l-4 border-amber-600">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <AlertCircle className="text-amber-600" size={28} />
            Important Reminders
          </h2>

          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Be Objective:</span> Answer based on what is actually implemented today, not what you plan to implement.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Collaborate:</span> Involve key stakeholders from your team to ensure accurate responses.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Save Progress:</span> You can save your assessment and return later to complete it.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Estimated Time:</span> The assessment typically takes 45-90 minutes to complete.</p>
            </li>
            <li className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-amber-600 text-white text-sm font-bold">•</div>
              </div>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Confidentiality:</span> Your responses are confidential and used only for improvement purposes.</p>
            </li>
          </ul>
        </div>

        <div className="flex justify-center gap-6 mb-12 flex-wrap">
          {/* Continue Button */}
          <button
            onClick={async () => {
              if (privacyAccepted) {
                await navigateToAssessmentOrDashboard("bcm", navigate);
              } else {
                navigate("/privacy-notice", {
                  state: { framework: "bcm" },
                });
              }
            }}
            className="flex items-center justify-center gap-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition transform hover:scale-105"
          >
            <Shield size={24} />
            <span>Continue to Assessment</span>
          </button>
        </div>

        <div className="text-center py-8 border-t border-gray-200">
          <p className="text-gray-600 mb-2">Questions or need assistance?</p>
          <p className="text-gray-500 text-sm">Contact your BCM administrator or refer to the comprehensive user guide.</p>
        </div>
      </main>
    </div>
  );
};

export default BCMInstructions;